import { getGenerativeModel } from 'firebase/ai';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseAI, db, auth } from './firebase';

// ── AI/automation engineering blog system prompt ──────────────────────────────
// testlogichub.com (the prior Databricks-certification product this prompt
// targeted) is no longer in production — see GOAL.md pillar 6 for the funnel
// this now serves (inbound AI/automation consulting/freelance leads).
const SYSTEM_PROMPT = `
You are an expert AI/automation engineering blog writer for DataLogicHub.
You write for hands-on data/ML/automation engineers and the technical decision-makers who might hire one.

PRIMARY GOAL: Every post demonstrates real, hands-on AI/automation engineering expertise and earns inbound consulting or freelance inquiries for DataLogicHub's owner — never a course or SaaS pitch, and never mentions testlogichub.com or Databricks certification (that product line is no longer in production).

CONTENT RULES:
- Concrete and specific: a pattern, a pitfall, a before/after, a real result — never generic AI news commentary
- No fluff — every sentence earns its place
- Write "you" not "one"; direct and practical, not academic
- Paragraphs: 2–4 lines maximum
- Use H2 for main sections, H3 for subsections
- Bold key terms on first use
- Code/config samples in fenced code blocks with language tags

STRUCTURE:
Title: specific and outcome-oriented, includes the year 2026 where it reads naturally
Introduction (100–150 words): open with a concrete result, number, or problem; state what the reader will walk away able to do
Body (1,000–1,800 words): 3–5 H2 sections, each 2–4 paragraphs, with code/examples where relevant
Conclusion (100–150 words): 2–3 key takeaways + the required CTA

REQUIRED CTA (include exactly once, in the conclusion only):
"Working on something like this? [Let's talk about your project](https://datalogichub.web.app/work-with-me) — I take on AI/automation consulting and freelance engagements."

NEVER use generic CTAs like "click here". ALWAYS use descriptive anchor text.
`.trim();

const POST_TYPE_GUIDANCE = {
  'playbook':    "Primary angle: a repeatable, step-by-step operational playbook (e.g. structuring a multi-agent pipeline, running a weekly content loop). Reader should be able to follow it end to end.",
  'concept':     "Primary angle: deep-dive on one AI/automation engineering concept (agents, RAG, workflow orchestration, LLM ops, prompt versioning, etc.) with concrete implementation detail.",
  'case-study':  "Primary angle: a real project outcome or lesson learned — what was built, what broke, what the fix was, and the measurable result.",
  'comparison':  "Primary angle: compare two tools/approaches for an AI/automation task (e.g. a no-code automation tool vs. a custom pipeline) with a clear recommendation.",
  'how-to':      "Primary angle: a step-by-step build guide for one concrete AI/automation task, runnable by the reader.",
};

// ── Parse title, excerpt, slug from generated markdown ───────────────────────
function parseGenerated(text) {
  const titleMatch = text.match(/^#\s+(.+)/m);
  const title = titleMatch ? titleMatch[1].trim() : 'AI & Automation Insights 2026';

  const metaMatch = text.match(/<!--\s*meta[:\s]+(.+?)\s*-->/i);
  const metaDescription = metaMatch ? metaMatch[1].trim() : title.slice(0, 155);

  const excerptMatch = text.match(/^(?!#)(?!\s*$)(.{50,})/m);
  const excerpt = excerptMatch
    ? excerptMatch[1].slice(0, 200).replace(/\*\*/g, '').trim() + '…'
    : title;

  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);

  return { title, excerpt, slug, metaDescription, category: 'AI & Automation' };
}

/**
 * Generate an AI/automation engineering blog post with Firebase AI Logic and save to Firestore.
 * Requires the caller to be signed in with role=admin (checked client-side; also
 * enforced by Firestore security rules on the blog_posts collection).
 *
 * @param {object} params
 * @param {string}  params.topic      - Blog topic / title idea
 * @param {string}  params.postType   - playbook | concept | case-study | comparison | how-to
 * @param {string}  params.keywords   - Optional extra SEO keywords
 * @param {boolean} params.published  - Publish immediately (true) or save as draft (false)
 * @returns {Promise<{ success: boolean, id: string, title: string, slug: string, content: string, published: boolean }>}
 */
export async function generateBlog({ topic, postType = 'concept', keywords = '', published = false }) {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to generate blogs.');

  const typeGuidance = POST_TYPE_GUIDANCE[postType] || POST_TYPE_GUIDANCE['concept'];

  const userPrompt = `
Write a complete blog post for DataLogicHub about the following AI/automation engineering topic.

TOPIC: ${topic.trim()}
POST TYPE: ${postType}
TYPE GUIDANCE: ${typeGuidance}
${keywords ? `ADDITIONAL KEYWORDS TO INCLUDE: ${keywords}` : ''}

OUTPUT FORMAT — return raw Markdown only, no wrapping code fences:
- First line must be the H1 title: # [Your SEO Title Here]
- Then add an HTML comment with meta description: <!-- meta: [155 char description] -->
- Then the full blog post body in Markdown
- All code examples in fenced code blocks with language tags (e.g. \`\`\`python)
- End the conclusion with the required consulting/freelance CTA (see system prompt)
  `.trim();

  // Create a model instance via Firebase AI Logic (Gemini Developer API)
  const model = getGenerativeModel(firebaseAI, {
    model: 'gemini-2.5-flash-lite',
    systemInstruction: SYSTEM_PROMPT,
    generationConfig: {
      temperature: 0.7,
      maxOutputTokens: 4096,
    },
  });

  const result = await model.generateContent(userPrompt);
  const generatedText = result.response.text();

  if (!generatedText || generatedText.length < 200) {
    throw new Error('AI returned empty or too-short content. Please try again.');
  }

  const { title, excerpt, slug, metaDescription, category } = parseGenerated(generatedText);

  // Tag every work-with-me link with UTM params so each lead traces back to
  // the exact post that drove it (WorkWithMe.jsx captures these into
  // consulting_leads.source). Mirrors functions/lib/generateBlog.js.
  const utmParams =
    `utm_source=blog&utm_medium=organic` +
    `&utm_campaign=${encodeURIComponent(slug)}` +
    `&utm_content=${encodeURIComponent(postType)}`;
  const trackedContent = generatedText.replace(
    /https:\/\/datalogichub\.web\.app\/work-with-me\/?(\?[^\s)]*)?/g,
    `https://datalogichub.web.app/work-with-me?${utmParams}`,
  );

  // Save to Firestore blog_posts collection
  const docRef = await addDoc(collection(db, 'blog_posts'), {
    title,
    excerpt,
    content: trackedContent,
    category,
    slug,
    metaDescription,
    imageUrl: null,
    videoUrl: null,
    published: Boolean(published),
    topic: topic.trim(),
    postType,
    keywords,
    generatedBy: 'gemini-2.5-flash-lite',
    authorUid: user.uid,
    createdAt: serverTimestamp(),
  });

  return {
    success: true,
    id: docRef.id,
    title,
    slug,
    content: trackedContent,
    published: Boolean(published),
  };
}
